import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, ArrowRight } from "lucide-react";
import { fetchClients, absoluteUrl } from "@/lib/api";

export default function ClientsCarousel() {
  const [clients, setClients] = useState([]);

  useEffect(() => {
    fetchClients().then(setClients).catch(() => setClients([]));
  }, []);

  // Duplicate list for seamless marquee
  const loop = clients.length ? [...clients, ...clients] : [];

  return (
    <section id="clients" data-testid="clients-section" className="py-24 md:py-32 bg-gradient-to-b from-blue-50/40 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <p className="text-sm uppercase tracking-widest font-semibold text-emerald-600 mb-3">Suppliers We Work With</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-slate-900">
            Nationwide Distributors (Full Provincial Coverage)
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            These companies have local hubs, sales forces, and logistics networks that deliver to private and public hospitals across Luzon, Visayas, and Mindanao.
          </p>
        </motion.div>

        {/* Marquee */}
        <div className="relative">
          <div aria-hidden className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white to-transparent z-10" />
          <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white to-transparent z-10" />
          <div className="overflow-hidden">
            <div className="flex gap-5 w-max animate-marquee">
              {loop.map((c, i) => (
                <div
                  key={`${c.id}-${i}`}
                  data-testid={`client-logo-${c.id}`}
                  className="shrink-0 w-72 bg-white border border-slate-200 rounded-2xl p-6 flex items-center gap-4 hover:border-blue-200 hover:shadow-md transition-all"
                >
                  {c.logo_url ? (
                    <img src={absoluteUrl(c.logo_url)} alt={c.name} className="w-14 h-14 rounded-xl object-cover bg-white border border-slate-100 shrink-0" />
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-600 to-emerald-500 text-white font-heading font-bold text-lg flex items-center justify-center shrink-0">
                      {c.logo_text}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-slate-900 text-sm truncate">{c.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                      <Building2 size={12} /> {c.type}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="text-center mt-12">
          <Link
            to="/clients"
            data-testid="clients-see-all"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium group"
          >
            See all partners & case studies
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
}
